import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Users, Mail, Phone, MapPin, ShoppingBag,
  DollarSign, Calendar, ChevronRight, UserPlus, LogIn
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_CUSTOMER_FORM = {
  name: '',
  email: '',
  password: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
};

const AdminCustomers = () => {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState(EMPTY_CUSTOMER_FORM);
  const [impersonatingCustomerId, setImpersonatingCustomerId] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/store/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
    setLoading(false);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleCreateCustomer = async () => {
    if (!customerForm.name.trim() || !customerForm.email.trim() || !customerForm.password.trim()) {
      toast({
        title: 'Missing required fields',
        description: 'Name, email, and password are required.',
        variant: 'destructive',
      });
      return;
    }

    setCreatingCustomer(true);
    try {
      const response = await axios.post(`${API}/admin/customers`, customerForm, {
        headers: getAuthHeaders(),
      });

      toast({
        title: 'Customer created',
        description: response.data?.email,
      });

      setCreateCustomerOpen(false);
      setCustomerForm(EMPTY_CUSTOMER_FORM);
      await fetchCustomers();
    } catch (error) {
      toast({
        title: 'Failed to create customer',
        description: error.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleImpersonateCustomer = async (customer) => {
    setImpersonatingCustomerId(customer.id);
    try {
      const response = await axios.post(
        `${API}/admin/customers/${customer.id}/impersonate`,
        {},
        { headers: getAuthHeaders() }
      );

      const { access_token, user } = response.data;
      const result = await startImpersonation(access_token, user);
      if (!result.success) {
        throw new Error(result.error || 'Could not start impersonation');
      }

      toast({
        title: 'Impersonation active',
        description: `Now signed in as ${user?.email || customer.email}`,
      });
      window.location.assign('/account');
    } catch (error) {
      toast({
        title: 'Impersonation failed',
        description: error.response?.data?.detail || error.message || 'Unable to impersonate this customer.',
        variant: 'destructive',
      });
    } finally {
      setImpersonatingCustomerId('');
    }
  };

  const handleViewCustomer = (customer) => {
    navigate(`/admin/customers/${customer.id}`);
  };

  const filteredCustomers = customers.filter(customer => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return customer.name.toLowerCase().includes(query) ||
             customer.email.toLowerCase().includes(query);
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        <p className="text-gray-500">View and manage your customer base</p>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => setCreateCustomerOpen(true)}
          className="bg-[#6e2ea8] hover:bg-[#5c2591] text-white"
          data-testid="open-create-customer-button"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Create Customer
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Customers</p>
                <p className="text-2xl font-bold">{customers.length}</p>
              </div>
              <Users className="w-10 h-10 text-[#6e2ea8]/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold">
                  ${customers.reduce((sum, c) => sum + (c.total_spent || 0), 0).toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-[#6e2ea8]/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold">
                  {customers.reduce((sum, c) => sum + (c.total_orders || 0), 0)}
                </p>
              </div>
              <ShoppingBag className="w-10 h-10 text-[#6e2ea8]/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search customers by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#6e2ea8] border-t-transparent rounded-full" />
            </div>
          ) : filteredCustomers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Customer</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Contact</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Location</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Orders</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Total Spent</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <tr key={customer.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#6e2ea8] rounded-full flex items-center justify-center text-white font-medium">
                            {customer.name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium">{customer.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="space-y-1">
                          <p className="text-sm flex items-center gap-1">
                            <Mail className="w-3 h-3 text-gray-400" /> {customer.email}
                          </p>
                          {customer.phone && (
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {customer.phone}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {customer.city && customer.state ? (
                          <span className="text-sm text-gray-500">
                            {customer.city}, {customer.state}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{customer.total_orders || 0} orders</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-semibold text-green-600">
                          ${customer.total_spent?.toFixed(2) || '0.00'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleImpersonateCustomer(customer)}
                            disabled={impersonatingCustomerId === customer.id}
                            data-testid={`impersonate-customer-button-${customer.id}`}
                          >
                            <LogIn className="w-4 h-4 mr-1" />
                            {impersonatingCustomerId === customer.id ? 'Impersonating...' : 'Impersonate'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewCustomer(customer)}
                            data-testid={`view-customer-button-${customer.id}`}
                          >
                            View <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No customers found</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createCustomerOpen} onOpenChange={setCreateCustomerOpen}>
        <DialogContent className="max-w-xl" data-testid="create-customer-dialog">
          <DialogHeader>
            <DialogTitle>Create Customer</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="create-customer-name">Full Name</Label>
              <Input
                id="create-customer-name"
                value={customerForm.name}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, name: e.target.value }))}
                data-testid="create-customer-name-input"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="create-customer-email">Email</Label>
              <Input
                id="create-customer-email"
                type="email"
                value={customerForm.email}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, email: e.target.value }))}
                data-testid="create-customer-email-input"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="create-customer-password">Password</Label>
              <Input
                id="create-customer-password"
                type="password"
                value={customerForm.password}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, password: e.target.value }))}
                data-testid="create-customer-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-customer-phone">Phone</Label>
              <Input
                id="create-customer-phone"
                value={customerForm.phone}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, phone: e.target.value }))}
                data-testid="create-customer-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-customer-zip">ZIP</Label>
              <Input
                id="create-customer-zip"
                value={customerForm.zip_code}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, zip_code: e.target.value }))}
                data-testid="create-customer-zip-input"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="create-customer-address">Address</Label>
              <Input
                id="create-customer-address"
                value={customerForm.address}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, address: e.target.value }))}
                data-testid="create-customer-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-customer-city">City</Label>
              <Input
                id="create-customer-city"
                value={customerForm.city}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, city: e.target.value }))}
                data-testid="create-customer-city-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-customer-state">State</Label>
              <Input
                id="create-customer-state"
                value={customerForm.state}
                onChange={(e) => setCustomerForm((prev) => ({ ...prev, state: e.target.value }))}
                data-testid="create-customer-state-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCreateCustomerOpen(false);
                setCustomerForm(EMPTY_CUSTOMER_FORM);
              }}
              data-testid="create-customer-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleCreateCustomer}
              disabled={creatingCustomer}
              className="bg-[#6e2ea8] hover:bg-[#5c2591]"
              data-testid="create-customer-submit-button"
            >
              {creatingCustomer ? 'Creating...' : 'Create Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomers;
